'use client';

import { useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useRegional } from '@/components/providers/regional-provider';
import { useDebounce } from '@/lib/hooks/use-debounce';
import { useAuth } from '@/hooks/use-auth';
import {
  useLeaderboardParticipants,
  useLeaderboardParticipantsSprint,
  useLeaderboardPillions,
  useLeaderboardTeams,
  useLeaderboardCountries,
  useLeaderboardCities,
  useLeaderboardCampaigns,
  useLeaderboardEvents,
  useLeaderboardStats,
  useMyParticipantRank,
} from '@/lib/queries/leaderboard';
import { LeaderboardHero } from './components/LeaderboardHero';
import { LeaderboardStatsBanner } from './components/LeaderboardStatsBanner';
import { LeaderboardTabs } from './components/LeaderboardTabs';
import { LeaderboardFilters } from './components/LeaderboardFilters';
import { LeaderboardSearch } from './components/LeaderboardSearch';
import { SprintToggleBar } from './components/SprintToggleBar';
import { getDefaultSprintId } from './helpers';
import { EntrantLeaderboardTable } from './components/EntrantLeaderboardTable';
import { TeamLeaderboardTable } from './components/TeamLeaderboardTable';
import { CountryLeaderboardTable } from './components/CountryLeaderboardTable';
import { CityLeaderboardTable } from './components/CityLeaderboardTable';
import { LeaderboardPagination } from './components/LeaderboardPagination';
import { MyPositionBanner } from './components/MyPositionBanner';
import { LEADERBOARD_PAGE_SIZE } from './constants';
import { RankFreshness } from '@/tenants/_shared/components/RankFreshness';
import type { LeaderboardConfig, LeaderboardSlots, LeaderboardTab } from './types';

interface LeaderboardViewProps {
  config: LeaderboardConfig;
  slots?: LeaderboardSlots;
  prefetchedData?: {
    campaigns: unknown;
    stats: unknown;
    initialParticipants: unknown;
    currencyCode: string;
  };
  initialCountry?: string;
  initialEvent?: string;
}

export function LeaderboardView({
  config,
  slots,
  prefetchedData,
  initialCountry,
  initialEvent,
}: LeaderboardViewProps) {
  const t = useTranslations('leaderboard');
  const tCommon = useTranslations('common');
  const { currency } = useRegional();
  const { entrant } = useAuth({ hydrateFromQuery: true });
  const tableRef = useRef<HTMLDivElement>(null);

  // Tab + filter state
  const [activeTab, setActiveTab] = useState<LeaderboardTab>('participants');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<string | undefined>(initialCountry);
  const [selectedEvent, setSelectedEvent] = useState<string | undefined>(initialEvent);
  const [sortBy, setSortBy] = useState<'raised' | 'participants' | 'donations'>('raised');
  const [sprintMode, setSprintMode] = useState(false);
  const [selectedSprintId, setSelectedSprintId] = useState<string>(() =>
    getDefaultSprintId(config.sprints),
  );

  const selectedSprint = config.sprints.find((s) => s.id === selectedSprintId) ?? config.sprints[0];

  const debouncedSearch = useDebounce(searchQuery, 300);

  const currencyMatch = prefetchedData?.currencyCode === currency;

  // Reference data
  const { data: campaigns } = useLeaderboardCampaigns(prefetchedData?.campaigns);
  const activeCampaign = campaigns?.[0];
  const campaignId = activeCampaign?.id;
  const fallbackImageUrl = activeCampaign?.eventThumbnailUrl ?? null;
  const { data: events } = useLeaderboardEvents(campaignId);

  // Stats banner — only fetched when the tenant enables it
  const statsQuery = useLeaderboardStats(
    config.showStatsBanner && campaignId ? { campaignId, currencyCode: currency } : undefined,
    currencyMatch ? prefetchedData?.stats : undefined,
  );

  // Build query inputs per tab type
  const entrantQueryInput = campaignId
    ? {
        campaignId,
        currencyCode: currency,
        countryCode: selectedCountry,
        eventId: selectedEvent,
        search: debouncedSearch || undefined,
        page: currentPage,
        pageSize: LEADERBOARD_PAGE_SIZE,
      }
    : undefined;

  const countryQueryInput = campaignId
    ? {
        campaignId,
        currencyCode: currency,
        search: debouncedSearch || undefined,
        sortBy,
        page: currentPage,
        pageSize: LEADERBOARD_PAGE_SIZE,
      }
    : undefined;

  const cityQueryInput = campaignId
    ? {
        campaignId,
        currencyCode: currency,
        countryCode: selectedCountry,
        search: debouncedSearch || undefined,
        sortBy,
        page: currentPage,
        pageSize: LEADERBOARD_PAGE_SIZE,
      }
    : undefined;

  // Pass initialData only on default view (page 1, participants tab, no filters, matching currency)
  const isDefaultView =
    activeTab === 'participants' &&
    currentPage === 1 &&
    !debouncedSearch &&
    !selectedCountry &&
    !selectedEvent &&
    currencyMatch;

  // Only the active tab fires its query — and only one of regular/sprint at a time
  const participants = useLeaderboardParticipants(
    activeTab === 'participants' && !sprintMode ? entrantQueryInput : undefined,
    isDefaultView && !sprintMode ? prefetchedData?.initialParticipants : undefined,
  );

  const sprintParticipants = useLeaderboardParticipantsSprint(
    activeTab === 'participants' && sprintMode && campaignId && selectedSprint
      ? {
          campaignId,
          currencyCode: currency,
          sprintStart: selectedSprint.bucketStart,
          sprintEnd: selectedSprint.bucketEnd,
          page: currentPage,
          pageSize: LEADERBOARD_PAGE_SIZE,
        }
      : undefined,
  );

  const activeParticipants = sprintMode ? sprintParticipants : participants;
  const passengers = useLeaderboardPillions(
    activeTab === 'passengers' ? entrantQueryInput : undefined,
  );
  const activeEntrants = activeTab === 'passengers' ? passengers : activeParticipants;
  const teams = useLeaderboardTeams(activeTab === 'teams' ? entrantQueryInput : undefined);
  const countriesQuery = useLeaderboardCountries(
    activeTab === 'countries' ? countryQueryInput : undefined,
  );
  const citiesQuery = useLeaderboardCities(activeTab === 'cities' ? cityQueryInput : undefined);

  // My position query (entrant tabs only; passenger tab only returns data for passenger entrants)
  const myRank = useMyParticipantRank(
    (activeTab === 'participants' || activeTab === 'passengers') &&
      !sprintMode &&
      campaignId &&
      entrant?.id
      ? {
          entrantId: entrant.id,
          campaignId,
          currencyCode: currency,
          countryCode: selectedCountry,
          eventId: selectedEvent,
          search: debouncedSearch || undefined,
          entrantFilter: activeTab === 'passengers' ? 'passenger' : 'all',
          pageSize: LEADERBOARD_PAGE_SIZE,
        }
      : undefined,
  );

  // Check if entrant is on current page
  const entrantOnCurrentPage =
    entrant?.id && activeEntrants.data?.items.some((i) => i.id === entrant.id);

  // Active query data for pagination / results count
  const activeQuery =
    activeTab === 'participants'
      ? activeParticipants
      : activeTab === 'passengers'
        ? passengers
        : activeTab === 'teams'
          ? teams
          : activeTab === 'countries'
            ? countriesQuery
            : citiesQuery;
  const totalPages = activeQuery.data?.totalPages ?? 0;
  const totalCount = activeQuery.data?.totalCount ?? 0;

  // Filter visibility per tab — sprint mode hides filters and search
  const showCountryFilter =
    !sprintMode &&
    (activeTab === 'participants' ||
      activeTab === 'passengers' ||
      activeTab === 'teams' ||
      activeTab === 'cities');
  const showEventFilter =
    !sprintMode &&
    (activeTab === 'participants' || activeTab === 'passengers' || activeTab === 'teams');
  const showFilters = showCountryFilter || showEventFilter;
  const showSprintBar = activeTab === 'participants' && config.sprints.length > 0;

  // Reset page on filter changes
  const handleTabChange = (tab: LeaderboardTab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    if (tab !== 'participants') setSprintMode(false);
  };

  const handleSprintToggle = (enabled: boolean) => {
    setSprintMode(enabled);
    setCurrentPage(1);
  };

  const handleSelectSprint = (id: string) => {
    setSelectedSprintId(id);
    setCurrentPage(1);
  };

  const handleCountryChange = (code: string | undefined) => {
    setSelectedCountry(code);
    setSelectedEvent(undefined);
    setCurrentPage(1);
  };

  const handleEventChange = (id: string | undefined) => {
    setSelectedEvent(id);
    setCurrentPage(1);
  };

  const handleSortChange = (sort: 'raised' | 'participants' | 'donations') => {
    setSortBy(sort);
    setCurrentPage(1);
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <main className="flex-1">
      {slots?.hero ?? <LeaderboardHero imageSrc={config.heroImageSrc} />}

      {config.showStatsBanner &&
        (slots?.statsBanner ?? (
          <LeaderboardStatsBanner data={statsQuery.data} isLoading={statsQuery.isLoading} />
        ))}

      <section className={`py-8 ${config.sectionClassName ?? 'bg-stone-50'}`}>
        <div className="container max-w-6xl">
          {/* Sticky control bar */}
          <div
            className={`sticky top-5 z-20 ${config.controlBarClassName ?? 'bg-stone-100'} -mx-4 px-3 md:mx-0 md:px-4 rounded-xl py-2.5`}
          >
            <div className="flex items-center gap-2">
              <LeaderboardTabs
                tabs={config.tabs}
                labelKeys={config.tabLabelKeys}
                activeTab={activeTab}
                onTabChange={handleTabChange}
              />

              {showFilters && (
                <div className="hidden md:flex items-center gap-2 border-l border-stone-200/60 pl-3 ml-1">
                  <LeaderboardFilters
                    events={events}
                    selectedCountry={selectedCountry}
                    selectedEvent={selectedEvent}
                    onCountryChange={handleCountryChange}
                    onEventChange={handleEventChange}
                    showCountryFilter={showCountryFilter}
                    showEventFilter={showEventFilter}
                  />
                </div>
              )}

              {!sprintMode && (
                <div className="ml-auto">
                  <LeaderboardSearch
                    searchQuery={searchQuery}
                    onSearchChange={handleSearchChange}
                  />
                </div>
              )}
            </div>

            {showSprintBar && (
              <SprintToggleBar
                sprints={config.sprints}
                selectedSprintId={selectedSprintId}
                onSelectSprint={handleSelectSprint}
                enabled={sprintMode}
                onToggle={handleSprintToggle}
              />
            )}

            {showFilters && (
              <div className="flex md:hidden items-center gap-2 mt-2 pt-2 border-t border-stone-200/40">
                <LeaderboardFilters
                  events={events}
                  selectedCountry={selectedCountry}
                  selectedEvent={selectedEvent}
                  onCountryChange={handleCountryChange}
                  onEventChange={handleEventChange}
                  showCountryFilter={showCountryFilter}
                  showEventFilter={showEventFilter}
                />
              </div>
            )}
          </div>

          {/* Leaderboard Table */}
          <div
            ref={tableRef}
            className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden mt-4 -mx-4 md:mx-0"
          >
            {activeTab === 'participants' && (
              <EntrantLeaderboardTable
                data={activeParticipants.data}
                isLoading={activeParticipants.isLoading}
                currency={currency}
                highlightEntrantId={entrantOnCurrentPage ? entrant?.id : undefined}
                fallbackImageUrl={fallbackImageUrl}
                rankBadges={config.rankBadges}
              />
            )}
            {activeTab === 'passengers' && (
              <EntrantLeaderboardTable
                data={passengers.data}
                isLoading={passengers.isLoading}
                currency={currency}
                highlightEntrantId={entrantOnCurrentPage ? entrant?.id : undefined}
                fallbackImageUrl={fallbackImageUrl}
                rankBadges={config.rankBadges}
              />
            )}
            {activeTab === 'teams' && (
              <TeamLeaderboardTable
                data={teams.data}
                isLoading={teams.isLoading}
                currency={currency}
                fallbackImageUrl={fallbackImageUrl}
                rankBadges={config.rankBadges}
              />
            )}
            {activeTab === 'countries' && (
              <CountryLeaderboardTable
                data={countriesQuery.data}
                isLoading={countriesQuery.isLoading}
                currency={currency}
                sortBy={sortBy}
                onSortChange={handleSortChange}
                rankBadges={config.rankBadges}
              />
            )}
            {activeTab === 'cities' && (
              <CityLeaderboardTable
                data={citiesQuery.data}
                isLoading={citiesQuery.isLoading}
                currency={currency}
                sortBy={sortBy}
                onSortChange={handleSortChange}
                columnLabelKey={config.cityColumnLabelKey}
                rankBadges={config.rankBadges}
              />
            )}

            {/* Your Position Banner — hidden in sprint mode */}
            {(activeTab === 'participants' || activeTab === 'passengers') &&
              !sprintMode &&
              myRank.data &&
              !entrantOnCurrentPage && (
                <MyPositionBanner
                  position={myRank.data}
                  currency={currency}
                  onJumpToPage={handlePageChange}
                  rankBadges={config.rankBadges}
                />
              )}

            {/* Results Count + Pagination */}
            {totalCount > 0 && (
              <div className="border-t border-stone-100 px-4 py-2 text-sm text-stone-500">
                <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                  <p>
                    {t('showingResults', {
                      start: (currentPage - 1) * LEADERBOARD_PAGE_SIZE + 1,
                      end: Math.min(currentPage * LEADERBOARD_PAGE_SIZE, totalCount),
                      total: totalCount,
                    })}
                  </p>
                  <RankFreshness rankMeta={activeQuery.data?.rankMeta} />
                </div>
              </div>
            )}

            {totalPages > 1 && (
              <LeaderboardPagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                previousLabel={tCommon('previous')}
                nextLabel={tCommon('next')}
              />
            )}
          </div>
        </div>
      </section>
    </main>
  );
}
